<template>
  <AuthCompleteLayout class="migration-v5">
    <!-- ========== LEFT PANEL ========== -->
    <template #left>
      <div class="migration-v5__illustration">
        <span class="migration-v5__illustration-halo migration-v5__illustration-halo--outer" />
        <span class="migration-v5__illustration-halo migration-v5__illustration-halo--inner" />
        <svg
          class="migration-v5__illustration-svg"
          viewBox="-60 -60 120 120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Two-stack arrow conveying "platform → platform" -->
          <rect
            x="-44"
            y="-28"
            width="32"
            height="48"
            rx="6"
            fill="#ffffff"
            opacity="0.95"
          />
          <rect
            x="-38"
            y="-22"
            width="20"
            height="3"
            rx="1.5"
            fill="#94c840"
          />
          <rect
            x="-38"
            y="-14"
            width="14"
            height="2.5"
            rx="1.25"
            fill="#c7d2da"
          />
          <rect
            x="-38"
            y="-7"
            width="16"
            height="2.5"
            rx="1.25"
            fill="#c7d2da"
          />
          <rect
            x="-38"
            y="0"
            width="10"
            height="2.5"
            rx="1.25"
            fill="#c7d2da"
          />
          <rect
            x="12"
            y="-28"
            width="32"
            height="48"
            rx="6"
            fill="#ffffff"
          />
          <rect
            x="18"
            y="-22"
            width="20"
            height="3"
            rx="1.5"
            fill="#f0b72f"
          />
          <rect
            x="18"
            y="-14"
            width="14"
            height="2.5"
            rx="1.25"
            fill="#c7d2da"
          />
          <rect
            x="18"
            y="-7"
            width="16"
            height="2.5"
            rx="1.25"
            fill="#c7d2da"
          />
          <rect
            x="18"
            y="0"
            width="10"
            height="2.5"
            rx="1.25"
            fill="#c7d2da"
          />
          <!-- Arrow between the two stacks -->
          <path
            d="M-6,-4 L6,-4 L6,-8 L14,0 L6,8 L6,4 L-6,4 Z"
            fill="#94c840"
          />
        </svg>
      </div>

      <div class="migration-v5__narrative">
        <p class="migration-v5__narrative-eyebrow">
          {{ t('migrationV5.narrative.eyebrow') }}
        </p>
        <h1 class="migration-v5__narrative-headline">
          {{ narrativeHeadline }}
        </h1>
        <p class="migration-v5__narrative-subtitle">
          {{ narrativeSubtitle }}
        </p>
      </div>

      <DotsProgress
        :total="totalSteps"
        :current-index="currentIndex"
        class="migration-v5__progress"
      />
    </template>

    <template #left-footer>
      <SpmButton
        :label="t('migrationV5.helpCta')"
        icon="far fa-comments"
        class-style="p-button-text p-button-secondary"
        @click="openSupport"
      />
      <SpmButton
        :label="t('migrationV5.logoutCta')"
        class-style="p-button-text p-button-secondary"
        @click="handleLogout"
      />
    </template>

    <!-- ========== RIGHT BODY ========== -->
    <!-- `show-migration-warning` is only meaningful on ConnectStep (step 1)
         where it surfaces the "uninstall the V4 module first" banner.
         MigrationV5WaitStep simply ignores the attribute. -->
    <component
      :is="currentStep.component"
      :key="currentStep.id"
      :can-go-back="!isFirst && currentStep.id !== 'wait'"
      :show-migration-warning="currentStep.id === 'connect'"
      @next="handleNext"
      @back="handleBack"
    />

    <!-- ========== RIGHT FOOTER (step footer portal) ========== -->
    <template #right-footer>
      <div id="onboarding-footer" />
    </template>
  </AuthCompleteLayout>
</template>

<script lang="ts">
import {
  computed, defineComponent, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Gleap from 'gleap';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import DotsProgress from '@/components/onboarding-v2/shared/DotsProgress.vue';
import ConnectStep from '@/components/onboarding-v2/steps/ConnectStep.vue';
import MigrationV5WaitStep from '@/components/onboarding-v2/steps/MigrationV5WaitStep.vue';
import AuthCompleteLayout from '@/views/layout/AuthCompleteLayout.vue';
import { Logout, UserState } from '@/composables/User';
import { showToastError, getErrorMessage } from '@/helpers';
import {
  fetchMigrationV5Status,
  notifyMigrationV5Ready,
} from '@/composables/migrationV5';

/**
 * Step descriptor table. We deliberately do NOT reuse `useOnboardingState`
 * here — it's tightly coupled to the 4-step new-shop onboarding (profile,
 * shop confirm, connect, lift-off) with persistence, validators and a
 * narrative-suffix engine we don't need. Migration V4 → V5 has only 2
 * states and they're driven entirely by the backend (connect → ready →
 * none). Local state is enough.
 */
const STEPS = [
  { id: 'connect', component: ConnectStep },
  { id: 'wait', component: MigrationV5WaitStep },
] as const;

/**
 * Body class toggled while this view is mounted. A non-scoped CSS rule
 * at the bottom of this SFC hides every automatic Gleap surface
 * (banners, modals, notifications, product tours) for as long as the
 * class is on `<body>`. Mirrors the pattern in `LayoutSubscription.vue`
 * — same problem: Gleap injects elements directly into `document.body`
 * outside the Vue component tree, so JS toggles like
 * `setDisableInAppNotifications(true)` race with the WebSocket push
 * and let banners slip through. CSS wins because it kicks in the
 * moment Gleap injects the node.
 */
const GLEAP_SILENT_BODY_CLASS = 'migration-v5-no-gleap-ui';

type StepId = (typeof STEPS)[number]['id'];

export default defineComponent({
  name: 'MigrationV5',

  components: {
    SpmButton,
    DotsProgress,
    ConnectStep,
    MigrationV5WaitStep,
    AuthCompleteLayout,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    const currentIndex = ref(0);
    const totalSteps = STEPS.length;

    const currentStep = computed(() => STEPS[currentIndex.value]);
    const isFirst = computed(() => currentIndex.value === 0);
    const isLast = computed(() => currentIndex.value === STEPS.length - 1);

    /**
     * Re-entry handling: pick the right starting step from the backend
     * status. If the module is already connected (status has advanced to
     * `ready_to_migrate_data` / `data_migration_in_progress` /
     * `data_migrated`), land directly on the WAIT step — re-doing the
     * connect step would be confusing. Otherwise (`in_progress`) start
     * on the connect step. ConnectStep stays idempotent regardless.
     */
    onMounted(async () => {
      // Hide every automatic Gleap surface for as long as the migration
      // view is mounted. CSS-based, so it covers banners injected AFTER
      // mount too — which JS calls like `setDisableInAppNotifications`
      // can't reliably stop (Gleap's WebSocket push can race past them).
      document.body.classList.add(GLEAP_SILENT_BODY_CLASS);

      try {
        Gleap.setLanguage(UserState.user?.lang ?? 'en');
        Gleap.initialize(process.env.VUE_APP_GLEAP_API_KEY || '');
        Gleap.setMaxNetworkRequests(50);
        Gleap.showFeedbackButton(false);
        Gleap.setDisableInAppNotifications(true);
        // Tear down a banner / modal that was already open before we
        // landed here (e.g. opened earlier by LayoutMain on the V4
        // side, before the V5 redirect).
        Gleap.closeBanner();
        Gleap.closeModal();
      } catch (e) {
        // Gleap init failure shouldn't block the migration
      }

      const waitStepIndex = STEPS.findIndex((s) => s.id === 'wait');
      try {
        const status = await fetchMigrationV5Status();
        const pastConnect = [
          'ready_to_migrate_data',
          'data_migration_in_progress',
          'data_migrated',
        ].includes(status);
        currentIndex.value = pastConnect && waitStepIndex >= 0
          ? waitStepIndex
          : 0;
      } catch (e) {
        currentIndex.value = 0;
      }
    });

    onBeforeUnmount(() => {
      // Restore Gleap defaults so the rest of the V5 app (LayoutMain)
      // keeps its banners and floating button active once the user
      // reaches their dashboard.
      document.body.classList.remove(GLEAP_SILENT_BODY_CLASS);
      try {
        Gleap.showFeedbackButton(true);
        Gleap.setDisableInAppNotifications(false);
      } catch {
        // Gleap may not be loaded — fail silently.
      }
    });

    const narrativeHeadline = computed(() => {
      const id = currentStep.value.id as StepId;
      return t(`migrationV5.narrative.${id}.headline`);
    });
    const narrativeSubtitle = computed(() => {
      const id = currentStep.value.id as StepId;
      return t(`migrationV5.narrative.${id}.subtitle`);
    });

    const handleNext = async () => {
      if (currentStep.value.id === 'connect') {
        // Fire the "ready" signal: backend persists state=ready and
        // sends the Telegram alert to the admin team. Done BEFORE
        // advancing the step so that if the call fails, the user
        // stays on step 1 with a toast and can retry.
        try {
          await notifyMigrationV5Ready();
        } catch (e: any) {
          showToastError(getErrorMessage(e));
          return;
        }
        currentIndex.value += 1;
        return;
      }
      // No-op on the wait step: it auto-advances via window.location.reload()
      // when the migration script finishes (cf. MigrationV5WaitStep).
    };

    const handleBack = () => {
      if (currentIndex.value > 0 && currentStep.value.id !== 'wait') {
        currentIndex.value -= 1;
      }
    };

    const handleLogout = async () => {
      await Logout();
      router.push({ name: 'login' });
    };

    const openSupport = () => {
      try {
        Gleap.open();
      } catch (e) {
        // noop
      }
    };

    return {
      t,
      currentIndex,
      totalSteps,
      currentStep,
      isFirst,
      isLast,
      narrativeHeadline,
      narrativeSubtitle,
      handleNext,
      handleBack,
      handleLogout,
      openSupport,
    };
  },
});
</script>

<style lang="scss" scoped>
.migration-v5 {
  &__illustration {
    position: relative;
    width: 220px;
    height: 220px;
    margin: 0 auto 1.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__illustration-halo {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;

    &--outer {
      width: 220px;
      height: 220px;
      background: radial-gradient(
        circle,
        rgba(148, 200, 64, 0.18) 0%,
        transparent 70%
      );
    }
    &--inner {
      width: 130px;
      height: 130px;
      background: radial-gradient(
        circle,
        rgba(148, 200, 64, 0.28) 0%,
        transparent 60%
      );
    }
  }

  &__illustration-svg {
    position: relative;
    width: 200px;
    height: 200px;
    z-index: 1;
  }

  &__narrative {
    text-align: center;
    margin-bottom: 2.5rem;
    max-width: 22rem;
    margin-left: auto;
    margin-right: auto;
  }

  &__narrative-eyebrow {
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 700;
    margin: 0 0 0.8rem;
  }

  &__narrative-headline {
    font-size: 1.55rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.2;
    margin: 0 0 0.8rem;
    letter-spacing: -0.01em;
  }

  &__narrative-subtitle {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.82);
    line-height: 1.55;
    margin: 0;
  }

  &__progress {
    margin: 0 auto;
  }
}
</style>

<!--
  Non-scoped: Gleap injects its banners / modals / tour overlays directly
  into `document.body`, outside this component's DOM tree. While the
  migration view is mounted, every automatic Gleap surface is hidden —
  only the conversation widget opened explicitly via Gleap.open() (the
  "Besoin d'aide ?" CTA) stays available. The body class is added on
  mount / removed on unmount, so the rest of the app (LayoutMain) is
  unaffected once the user lands on their V5 dashboard.
-->
<style lang="scss">
body.migration-v5-no-gleap-ui {
  .gleap-b,                       /* outbound banner */
  .gleap-modal,                   /* outbound modal */
  .gleap-modal-wrapper,
  .gleap-modal-backdrop,
  .gleap-notification-container,  /* in-app notification stack */
  .gleap-tooltip,                 /* product-tour tooltips / hotspots */
  .gleap-tour-message {           /* product tours */
    display: none !important;
  }
}
</style>
